import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b';
import SignUp from './SignUp';
import AccessRequest from './AccessRequest';
import PendingApproval from './PendingApproval';
import EULAAcceptance from './EULAAcceptance';
import OrgPicker from './OrgPicker';

console.log('🔄 LOGIN: Fresh component loaded');

const Login = ({ onSwitchToSignUp, onForgotPassword }) => {
  console.log('🔍 LOGIN: Rendering...');

  const navigate = useNavigate();
  const stytch = useStytchB2BClient();
  const { session } = useStytchMemberSession();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sentToEmail, setSentToEmail] = useState(null);
  const [showOrganizationSetup, setShowOrganizationSetup] = useState(false);
  const [showAccessRequest, setShowAccessRequest] = useState(false);
  const [showPendingApproval, setShowPendingApproval] = useState(false);
  const [showOrgPicker, setShowOrgPicker] = useState(false);
  const [intermediateSessionData, setIntermediateSessionData] = useState(null);

  // EULA state
  const [eulaConfig, setEulaConfig] = useState(null);
  const [eulaLoading, setEulaLoading] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [acceptedEulaVersion, setAcceptedEulaVersion] = useState(null);

  console.log('🔍 LOGIN: Stytch client available:', !!stytch);
  console.log('🔍 LOGIN: Current session:', session);

  // Handle session changes - redirect when authenticated
  useEffect(() => {
    if (session) {
      console.log('✅ LOGIN: Session established, navigating to dashboard...');
      console.log('🔍 LOGIN: Session details:', session);
      navigate('/', { replace: true });
    }
  }, [session, navigate]);

  // Fetch EULA content when needed (for org setup)
  // NOTE: /admin/eula/content is PUBLIC (no JWT) — org signup happens before a
  // session exists, which is exactly why the endpoint was made public. Same
  // endpoint the post-login gate uses, so signup and gate always show the same
  // document/version.
  useEffect(() => {
    if (showOrganizationSetup && !eulaConfig && !eulaLoading) {
      setEulaLoading(true);
      const baseURL = process.env.REACT_APP_API_URL;
      fetch(`${baseURL}/admin/eula/content`)
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(data => {
          console.log('🔍 LOGIN: EULA content loaded, version:', data.version);
          setEulaConfig(data);
        })
        .catch(err => {
          console.error('❌ LOGIN: Failed to load EULA content:', err);
          // Honest failure: NO fabricated version. With version=null, the
          // backend stores an empty eula_version at org creation, and the user
          // is gated (with real content) at first login instead. Never invent
          // a version label — it writes acceptance/audit records for a version
          // that never existed.
          setEulaConfig({ content: '', version: null, error: true });
        })
        .finally(() => setEulaLoading(false));
    }
  }, [showOrganizationSetup, eulaConfig, eulaLoading]);

  // Handle EULA acceptance
  const handleEulaAccept = (version) => {
    console.log('✅ LOGIN: EULA accepted, version:', version);
    setEulaAccepted(true);
    setAcceptedEulaVersion(version);
  };

  // Handle EULA decline
  const handleEulaDecline = () => {
    console.log('🔍 LOGIN: EULA declined, returning to login');
    setShowOrganizationSetup(false);
    setEulaAccepted(false);
    setIntermediateSessionData(null);
    setError('You must accept the End User License Agreement to continue.');
  };

  // Handle magic link token authentication
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const tokenType = urlParams.get('stytch_token_type');
    
    if (token && tokenType === 'discovery' && stytch) {
      console.log('🔍 LOGIN: Found discovery token, calling stytch.magicLinks.authenticate...');
      console.log('🔍 LOGIN: Token:', token);
      
      setIsLoading(true);
      setError(null);
      
      stytch.magicLinks.discovery.authenticate({
        discovery_magic_links_token: token,
      })
      .then((result) => {
        console.log('✅ LOGIN: Discovery authentication successful:', result);
        console.log('🔍 LOGIN: Intermediate session token:', result.intermediate_session_token);
        console.log('🔍 LOGIN: Email address:', result.email_address);
        console.log('🔍 LOGIN: Discovered organizations:', result.discovered_organizations);
        
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Find every organization the user can authenticate into
        const authenticatedOrgs = result.discovered_organizations.filter(org => org.member_authenticated);

        if (authenticatedOrgs.length > 1) {
          // User is a member of multiple orgs - let them choose which to enter.
          // Post-session gates (EULA, status) run per-org in ProtectedRoute after exchange.
          console.log('🔍 LOGIN: Multiple authenticated organizations - showing org picker');

          setIntermediateSessionData({
            intermediate_session_token: result.intermediate_session_token,
            email_address: result.email_address,
            discovered_organizations: authenticatedOrgs
          });
          setShowOrgPicker(true);
          setIsLoading(false);

        } else if (authenticatedOrgs.length === 1) {
          const authenticatedOrg = authenticatedOrgs[0];
          console.log('🔍 LOGIN: User authenticated in organization, exchanging intermediate session...');
          console.log('🔍 LOGIN: Organization ID:', authenticatedOrg.organization.organization_id);

          // Exchange intermediate session for full session
          return stytch.discovery.intermediateSessions.exchange({
            intermediate_session_token: result.intermediate_session_token,
            organization_id: authenticatedOrg.organization.organization_id,
            session_duration_minutes: 10080
          });
        } else {
          console.log('🔍 LOGIN: User not authenticated - checking for new user scenario...');
          console.log('🔍 LOGIN: Discovered organizations count:', result.discovered_organizations.length);

          // Check if this is a completely new user (no organizations)
          // or existing user requesting access to an organization
          if (result.discovered_organizations.length === 0) {
            console.log('🔍 LOGIN: New user detected - no organizations found');
            console.log('🔍 LOGIN: Showing organization setup for email:', result.email_address);

            // Store intermediate session data and show organization setup
            setIntermediateSessionData({
              intermediate_session_token: result.intermediate_session_token,
              email_address: result.email_address,
              discovered_organizations: result.discovered_organizations
            });
            setShowOrganizationSetup(true);
            setIsLoading(false);

          } else if (result.discovered_organizations.length === 1) {
            const discoveredOrg = result.discovered_organizations[0];
            console.log('🔍 LOGIN: Single organization found:', discoveredOrg.organization?.organization_name);
            console.log('🔍 LOGIN: Member authenticated:', discoveredOrg.member_authenticated);
            console.log('🔍 LOGIN: Membership:', discoveredOrg.membership);

            // Check if user is a pending member (created via JIT but not approved)
            // or if they need to request access
            if (discoveredOrg.membership && discoveredOrg.membership.type === 'pending_member') {
              console.log('🔍 LOGIN: User is pending member - showing pending message');
              setError('Your access request is pending approval from an administrator.');
              setIsLoading(false);
            } else if (!discoveredOrg.member_authenticated) {
              console.log('🔍 LOGIN: User not a member - showing access request');

              // Store intermediate session data and show access request
              setIntermediateSessionData({
                intermediate_session_token: result.intermediate_session_token,
                email_address: result.email_address,
                discovered_organizations: result.discovered_organizations
              });
              setShowAccessRequest(true);
              setIsLoading(false);
            } else {
              // This shouldn't happen - if member_authenticated is true,
              // it should have been caught in the first if block (line 63)
              console.log('⚠️ LOGIN: Unexpected state - member authenticated but not caught earlier');
              setError('Unexpected authentication state. Please try again.');
              setIsLoading(false);
            }

          } else {
            console.log('🔍 LOGIN: Multiple organizations found - this should not happen with domain matching');

            // Clear URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);

            setError('Multiple organizations found for your email domain. Please contact support.');
            setIsLoading(false);
          }
        }
      })
      .then((exchangeResult) => {
        if (exchangeResult) {
          console.log('✅ LOGIN: Session exchange successful:', exchangeResult);
          console.log('🔍 LOGIN: Session token:', exchangeResult.session_token);
          console.log('🔍 LOGIN: Member session:', exchangeResult.member_session);
          // Session should now be available via useStytchMemberSession hook
        }
      })
      .catch((error) => {
        console.error('❌ LOGIN: Magic link authentication failed:', error);
        
        // Safe logging to prevent console crashes
        const errorType = error?.error_type || 'unknown';
        const statusCode = error?.status_code || error?.status || 'unknown';
        
        console.log('🔍 LOGIN: Error type:', errorType);
        console.log('🔍 LOGIN: Error status:', statusCode);
        
        // Provide user-friendly error messages
        let userMessage = 'Authentication failed. Please try again.';
        
        if (errorType === 'unable_to_auth_magic_link') {
          userMessage = 'This magic link has expired or was already used. Please request a new one.';
        } else if (errorType === 'discovery_magic_link_invalid') {
          userMessage = 'Invalid magic link. Please request a new one.';
        } else if (errorType === 'network_error' || statusCode >= 500) {
          userMessage = 'Service temporarily unavailable. Please try again in a moment.';
        } else if (statusCode === 404) {
          userMessage = 'Invalid magic link. Please request a new one.';
        } else if (statusCode === 401) {
          userMessage = 'This magic link has expired or was already used. Please request a new one.';
        }
        
        console.log('🔍 LOGIN: User-friendly error message:', userMessage);
        
        // Clear URL parameters to prevent retry loops with bad tokens
        window.history.replaceState({}, document.title, window.location.pathname);
        
        setError(userMessage);
        setIsLoading(false);
      });
    }
  }, [stytch, navigate]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    setIsLoading(true);
    console.log('🚀 LOGIN: Starting login with email:', email);

    try {
      console.log('🔑 LOGIN: Calling Stytch discovery email send...');
      console.log('🔍 LOGIN: Email to send to:', email);
      console.log('🔍 LOGIN: Stytch client:', stytch);
      
      if (!stytch) {
        throw new Error('Stytch client not available');
      }
      
      console.log('🚀 LOGIN: Calling stytch.magicLinks.email.discovery.send...');
      const result = await stytch.magicLinks.email.discovery.send({
        email_address: email,
        discovery_redirect_url: `${window.location.origin}/authenticate`,
      });
      
      console.log('✅ LOGIN: Discovery email sent successfully!');
      console.log('🔍 LOGIN: Stytch SDK result:', result);

      setSuccess(true);
      setSentToEmail(email);

    } catch (error) {
      console.error('❌ LOGIN: Discovery email send failed:', error);
      console.error('🔍 LOGIN: Error type:', typeof error);
      console.error('🔍 LOGIN: Error message:', error.message);
      console.error('🔍 LOGIN: Error details:', error);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Failed to send email. Please try again.';
      console.log('🔍 LOGIN: Setting error message:', errorMessage);
      
      setError(errorMessage);
    } finally {
      console.log('🔍 LOGIN: Setting loading to false');
      setIsLoading(false);
    }
  };

  // Handle successful organization creation
  const handleOrganizationCreated = async (result) => {
    console.log('✅ LOGIN: Organization created');
    console.log('🔍 LOGIN: Organization creation result:', result);

    // Bootstrap the Stytch session from server-returned tokens
    // The SDK stores sessions in cookies - we set them manually
    if (result.session_token && result.session_jwt) {
      console.log('🔐 LOGIN: Setting session cookies from backend response...');

      // Set Stytch session cookies (these are the cookie names Stytch SDK uses)
      const maxAge = 60 * 60 * 24 * 7; // 7 days in seconds
      document.cookie = `stytch_session=${result.session_token}; path=/; max-age=${maxAge}; SameSite=Lax`;
      document.cookie = `stytch_session_jwt=${result.session_jwt}; path=/; max-age=${maxAge}; SameSite=Lax`;

      console.log('✅ LOGIN: Session cookies set, reloading to pick up session...');

      // Reload the page so Stytch SDK picks up the session from cookies
      try { localStorage.setItem('lumoz-last-reload-reason', JSON.stringify({ reason: 'post-login-session-bootstrap', at: Date.now() })); } catch {}
      window.location.reload();
      return;
    } else {
      console.log('⚠️ LOGIN: No session tokens in response');
      setError('Session not established. Please try logging in again.');
    }

    setShowOrganizationSetup(false);
  };

  // Handle organization creation errors
  const handleOrganizationError = (error) => {
    console.error('❌ LOGIN: Organization creation error:', error);
    setError('Failed to create organization. Please try again.');
    setShowOrganizationSetup(false);
    setIsLoading(false);
  };

  // Handle successful access request submission
  const handleAccessRequested = (result) => {
    console.log('✅ LOGIN: Access request submitted');
    setShowAccessRequest(false);
    setShowPendingApproval(true);
  };

  // Handle organization selection from the multi-org picker
  const handleOrgSelected = async (organizationId) => {
    console.log('🔍 LOGIN: Org selected from picker:', organizationId);
    setIsLoading(true);
    setError(null);

    try {
      // Exchange intermediate session for a full session scoped to the chosen org.
      // The session hook (useEffect on `session`) redirects to '/', where
      // ProtectedRoute applies any org-scoped gates (EULA, pending approval).
      await stytch.discovery.intermediateSessions.exchange({
        intermediate_session_token: intermediateSessionData.intermediate_session_token,
        organization_id: organizationId,
        session_duration_minutes: 10080
      });
    } catch (err) {
      console.error('❌ LOGIN: Org session exchange failed:', err);
      // Keep the user on the picker so they can retry (or pick another org)
      // without needing a fresh magic link - the intermediate session is still valid.
      setError('Could not sign in to that organization. Please try again.');
      setIsLoading(false);
    }
  };

  // Return from the org picker to the email entry screen
  const handleOrgPickerBack = () => {
    console.log('🔍 LOGIN: Returning from org picker to email entry');
    setShowOrgPicker(false);
    setIntermediateSessionData(null);
    setIsLoading(false);
    setError(null);
  };

  // Show EULA first, then signup form for new users
  if (showOrganizationSetup && intermediateSessionData) {
    // Show EULA dialog first if not yet accepted
    if (!eulaAccepted) {
      return (
        <EULAAcceptance
          eulaContent={eulaConfig?.content}
          eulaVersion={eulaConfig?.version}
          onAccept={handleEulaAccept}
          onDecline={handleEulaDecline}
          loading={eulaLoading}
        />
      );
    }

    // EULA accepted, show signup form
    return (
      <SignUp
        intermediateSessionToken={intermediateSessionData.intermediate_session_token}
        emailAddress={intermediateSessionData.email_address}
        eulaVersion={acceptedEulaVersion}
        onOrganizationCreated={handleOrganizationCreated}
        onError={handleOrganizationError}
      />
    );
  }

  // Show org picker when the user is a member of multiple organizations
  if (showOrgPicker && intermediateSessionData) {
    return (
      <OrgPicker
        organizations={intermediateSessionData.discovered_organizations}
        emailAddress={intermediateSessionData.email_address}
        onSelect={handleOrgSelected}
        onBack={handleOrgPickerBack}
        loading={isLoading}
        error={error}
      />
    );
  }

  // Show AccessRequest for existing org
  if (showAccessRequest && intermediateSessionData) {
    return (
      <AccessRequest
        organizationData={intermediateSessionData.discovered_organizations[0].organization}
        intermediateSessionToken={intermediateSessionData.intermediate_session_token}
        emailAddress={intermediateSessionData.email_address}
        onRequestSubmitted={handleAccessRequested}
        onError={(err) => setError(err.message)}
      />
    );
  }

  // Show PendingApproval after request submitted
  if (showPendingApproval) {
    return <PendingApproval />;
  }

  return (
    <div className="min-h-screen bg-background-primary flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">LUMOZ AI</h1>
          <p className="text-text-secondary">Log in &nbsp;/ &nbsp;Sign up</p>
        </div>

        {/* Email Form */}
        <div className="bg-background-secondary border border-accent-lavender/30 rounded-lg p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <span className="text-red-400 text-lg">⚠️</span>
                </div>
                <div className="flex-1">
                  <p className="text-red-400 text-sm font-medium">
                    {typeof error === 'string' ? error : String(error)}
                  </p>
                  {error.includes('expired') || error.includes('already used') ? (
                    <button
                      onClick={() => setError(null)}
                      className="mt-2 text-xs bg-red-400/20 text-red-300 px-2 py-1 rounded hover:bg-red-400/30 transition-colors"
                    >
                      Try with a new email
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded text-green-400 text-sm">
              Check your email for a link
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (success) {
                    setSuccess(false);
                    setSentToEmail(null);
                  }
                }}
                className="w-full px-3 py-2 bg-background-primary border border-border-subtle rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                placeholder="work@company.com"
                required
                disabled={isLoading}
              />
              <p className="text-text-muted text-xs mt-2">
                We'll send a secure login link to your email
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || (success && email === sentToEmail)}
              className="bg-accent-primary-base text-text-inverse py-2 px-6 rounded-full font-medium hover:bg-accent-primary-vibrant transition-colors disabled:opacity-50 disabled:cursor-not-allowed mx-auto block"
            >
              {isLoading ? 'Sending...' : 'Send Login Link'}
            </button>
          </form>

          {/* Unified flow - no separate signup needed */}
        </div>
      </div>
    </div>
  );
};

export default Login;
